import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css'; // ✅ Korrigierter Pfad zu styles/index.css
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { unregister as unregisterSW } from './serviceWorkerRegistration';
import './i18n'; // i18next initialisieren
import { VerbandProvider } from './context/VerbandContext';
import { enqueue as offlineEnqueue, flags as offlineFlags, labelFromUrl } from './utils/offlineQueue';

// ─── Globaler Fetch-Interceptor ───────────────────────────────────────────────
// Fügt bei allen /api/-Requests automatisch den Auth-Header ein und
// leitet bei 401 global zur Login-Seite weiter.
// Enthält 429-Back-off: nach einem Rate-Limit werden API-Calls für 3s pausiert.
// Enthält Offline-Queue: POST/PUT/DELETE die wegen Netzausfall fehlschlagen
//   werden in IndexedDB gespeichert und bei Reconnect automatisch nachgesendet.
(function installFetchInterceptor() {
  const originalFetch = window.fetch.bind(window);

  // Back-off: Timestamp bis wann API-Calls blockiert werden
  let rateLimitedUntil = 0;
  let isRedirecting = false; // Verhindert mehrfache 401-Redirects bei parallelen Requests

  const QUEUE_METHODS = new Set(['POST', 'PUT', 'DELETE', 'PATCH']);

  window.fetch = async (input, options = {}) => {
    const urlStr = input instanceof Request ? input.url : String(input);
    const isApiRequest = urlStr.startsWith('/api/');

    if (isApiRequest) {
      // 429 Back-off: blockiere API-Calls bis Back-off abgelaufen
      const now = Date.now();
      if (now < rateLimitedUntil) {
        return new Response(
          JSON.stringify({ success: false, error: 'Zu viele Anfragen – kurz warten', code: 'RATE_LIMIT_BACKOFF' }),
          { status: 429, headers: { 'Content-Type': 'application/json' } }
        );
      }

      const token = localStorage.getItem('token');

      if (token) {
        let headers = options.headers || {};
        if (headers instanceof Headers) {
          headers = Object.fromEntries(headers.entries());
        }
        if (!headers['Authorization'] && !headers['authorization']) {
          options = {
            ...options,
            headers: { ...headers, 'Authorization': `Bearer ${token}` }
          };
        }
      }

      // Super-Admin Verband-Switch: ?verband_id anhängen
      const activeVerbandId = localStorage.getItem('activeVerbandId');
      if (activeVerbandId) {
        const urlObj = new URL(urlStr, window.location.origin);
        if (!urlObj.searchParams.has('verband_id')) {
          urlObj.searchParams.set('verband_id', activeVerbandId);
          input = urlObj.pathname + urlObj.search;
        }
      }
    }

    let response;
    try {
      response = await originalFetch(input, options);
    } catch (networkError) {
      // Echter Netzwerkfehler (kein Internet) — schreibbare API-Calls in Queue
      const method = (options.method || 'GET').toUpperCase();
      if (isApiRequest && QUEUE_METHODS.has(method) && !offlineFlags.isReplaying) {
        const body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body || {});
        const label = labelFromUrl(urlStr, method);
        try {
          await offlineEnqueue({ url: urlStr, method, body, label });
          window.dispatchEvent(new Event('offline-queue-changed'));
          console.warn(`[OfflineQueue] ${method} ${urlStr} gespeichert (${label})`);
        } catch (dbErr) {
          console.error('[OfflineQueue] IndexedDB-Fehler:', dbErr);
        }
        // Synthetic success so scoring UIs don't show error dialogs
        return new Response(
          JSON.stringify({ success: true, queued: true, label }),
          { status: 200, headers: { 'Content-Type': 'application/json' } }
        );
      }
      throw networkError;
    }

    if (isApiRequest) {
      // 429-Back-off aktivieren: 3 Sekunden keine weiteren API-Calls
      if (response.status === 429) {
        rateLimitedUntil = Date.now() + 3000;
        console.warn('⏸ [RateLimit] 429 empfangen – API-Calls für 3s pausiert');
      }

      // Globales 401-Handling: Session abgelaufen → Logout (einmalig, kein Loop)
      if (response.status === 401) {
        const isHomePage = window.location.pathname === '/';
        if (!isHomePage && !isRedirecting) {
          isRedirecting = true;
          console.warn('🚪 [Auth] 401 – Session abgelaufen, automatischer Logout...');
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          localStorage.removeItem('clubData');
          window.location.href = '/';
        }
      }
    }

    return response;
  };
})();
// ─────────────────────────────────────────────────────────────────────────────

// Iframe-Guard: Wenn die App in einem fremden iframe läuft (z.B. Dojo-SuperAdmin-Vorschau),
// keine React-App initialisieren → verhindert unnötige API-Calls gegen das Rate-Limit
if (window !== window.top) {
  document.getElementById('root').innerHTML =
    '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#888;font-size:14px;">events.tda-intl.org – bitte direkt öffnen</div>';
} else {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <BrowserRouter>
      <VerbandProvider>
        <App />
      </VerbandProvider>
    </BrowserRouter>
  );

  // Service Worker deaktiviert — Updates sollen sofort sichtbar sein
  unregisterSW();
}


// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();